import { Table, TableCell } from '@design';
import { checkCellValueStyle, NumericalCellValueStyle } from '@helpers';
import { HeaderRow } from '@terminal/components/price-movement/components/price-movement-table/components/header-row/HeaderRow';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { PercentageCell } from '../percentage-cell/PercentageCell';
import styles from './PredictionSummaryTable.module.scss';

const backgroundColors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish-soft)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish-soft)',
  [NumericalCellValueStyle.Neutral]: 'transparent',
};

export interface PredictionSummaryTableProps {
  data: Record<string, PredictionRow>;
  isMedian?: boolean;
  horizons: string[];
  isFixedIncome: boolean;
}

export type PredictionRow = Record<string, number>;

export const PredictionSummaryTable = ({
  data,
  isMedian,
  horizons,
  isFixedIncome,
}: PredictionSummaryTableProps) => {
  const { t } = useTranslation('scenario');

  const headerLabels = horizons.map(horizon => {
    /* i18next-extract-mark-context-next-line  ["1w", "2w", "1m", "3m", "6m", "1y"] */
    return t('scenario:drawer.summaryTable.horizon', {
      context: horizon,
    });
  });

  const rowsIds = ['max', 'p80', isMedian ? 'median' : 'mean', 'p20', 'min'];

  const renderTableBody = () =>
    rowsIds.map(rowId => {
      const horizonCells = horizons.map(horizon => {
        const mappedPrediction = data[horizon];
        const value = mappedPrediction?.[rowId];

        return (
          <TableCell
            key={`body-${rowId}-${horizon}`}
            alignment="center"
            className={styles.numericalTableCell}
            data-numerical-cell
            style={
              {
                '--cell-bg': backgroundColors[checkCellValueStyle(value)],
              } as CSSProperties
            }
          >
            <PercentageCell
              suffix={isFixedIncome ? 'bps' : '%'}
              value={value}
            />
          </TableCell>
        );
      });

      horizonCells.unshift(
        <TableCell key={`body-${rowId}`} alignment="start" isNumber={false}>
          {
            <TooltipContainer
              showUnderline
              label={t(`scenario:drawer.summaryTable.tooltip`, {
                context: rowId,
              })}
            >
              {
                <span>
                  {
                    /* i18next-extract-mark-context-next-line  ["max", "p80", "median", "mean", "p20", "min"] */

                    t(`scenario:drawer.summaryTable.row`, {
                      context: rowId,
                    })
                  }
                </span>
              }
            </TooltipContainer>
          }
        </TableCell>
      );
      return (
        <tr key={rowId} data-testid={rowId}>
          {horizonCells}
        </tr>
      );
    });

  return (
    <div
      className={styles.predictionSummaryTableRoot}
      data-testid="prediction-summary-table"
    >
      <Table
        className={styles.tableStyled}
        withBodyRowHover={false}
        TableHeaderContent={<HeaderRow labels={['', ...headerLabels]} />}
        TableBodyContent={renderTableBody()}
      />
    </div>
  );
};
